// Base de la API. En dev queda '/api' (lo resuelve el proxy de Vite hacia la API local).
// En producción se inyecta VITE_API_BASE en el build (ej. https://grupot-api.ddns.net/sentdm).
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/api';

const TOKEN_KEY = 'bridge.courier.token';

export function saveToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

type ApiOk<T> = { success: true; data: T };
type ApiErr = { success: false; error: { code: string; message: string; detail?: unknown } };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  const token = getToken();
  if (token) headers.set('authorization', `Bearer ${token}`);

  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  const body = (await res.json()) as ApiOk<T> | ApiErr;
  if (!res.ok || !body.success) {
    const msg = !body.success ? body.error.message : `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return body.data;
}

export type DeliveryStatus = 'pending' | 'signed' | 'cancelled';

export type DeliveryItem = {
  id: string;
  numero_factura: string;
  destinatario_nombre: string | null;
  destinatario_phone: string | null;
  status: DeliveryStatus;
  created_at: string;
  signed_at: string | null;
  signed_lat: string | null;
  signed_lng: string | null;
  sent: boolean; // si ya se mandó el WhatsApp al cliente
  pdf_url: string;
  signed_url: string | null;
  whatsapp_url: string | null; // link wa.me con el mensaje del cliente
};

export type ListParams = {
  status?: DeliveryStatus | 'all';
  search?: string;
  from?: string; // YYYY-MM-DD
  to?: string;
};

export const api = {
  login(tenantId: number, courierUser: string, accessCode: string): Promise<{ token: string }> {
    return request('/courier/login', {
      method: 'POST',
      body: JSON.stringify({ tenant_id: tenantId, courier_user: courierUser, access_code: accessCode }),
    });
  },
  list(params: ListParams = {}): Promise<DeliveryItem[]> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    const s = qs.toString();
    return request(`/courier/deliveries${s ? `?${s}` : ''}`);
  },
  send(id: string): Promise<{ sent: boolean; view_url: string }> {
    return request(`/courier/deliveries/${id}/send`, { method: 'POST' });
  },
  sign(
    id: string,
    payload: {
      signature_png_base64: string;
      receptor_cedula: string;        // CI normalizada (8 dígitos sin separadores)
      location: { lat: number; lng: number; accuracy_m?: number };
    },
  ): Promise<{ id: string; status: string; signed_at: string; signed_url: string | null }> {
    return request(`/courier/deliveries/${id}/sign`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
};
